"use client";

import i18n from "i18next";
import { useContext } from "react";
import { I18nContext, I18nextProvider, initReactI18next } from "react-i18next";
import { defaultLocale } from "@/config/info";

import type { I18NProviderProps } from "./types";

const I18NProvider = ({ children, dictionaries = {} }: I18NProviderProps) => {
  if (!i18n.isInitialized) {
    i18n
      .use(initReactI18next)
      // .use(languageDetectorPlugin)
      .init({
        resources: dictionaries,
        compatibilityJSON: "v4",
        fallbackLng: defaultLocale,
        interpolation: {
          escapeValue: false
        }
      })
      .then(() => {
        console.log(`🔵 i18n initialized`);
      })
      .catch((err) => {
        console.log(`🔴 i18n init error`, err);
      });
  } else {
    // i18n is a module-level singleton, so .init() above only ever runs once
    // per server process (or per browser tab) — a fresh `dictionaries` from a
    // later render (e.g. after editing/adding files under public/i18n/**)
    // would otherwise be silently dropped forever. Re-sync every locale's
    // bundle into the already-initialized instance instead.
    for (const [lng, { translation }] of Object.entries(dictionaries)) {
      i18n.addResourceBundle(lng, "translation", translation, true, true);
    }
  }

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
};

export const useI18N = () => {
  const { i18n } = useContext(I18nContext);
  if (!i18n) {
    throw new Error("useI18N must be used within a I18NProvider");
  }
  return i18n;
};

export default I18NProvider;
